// @ts-strict-ignore
import { ColumnType } from 'antd/lib/table';
import Table from 'antd/lib/table/Table';
import * as React from 'react';
import { OrgComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import {
    AzureActiveDirectoryTeam,
    AzureTeamTag,
    IntegrationProviderAzureActiveDirectory,
    UserGroup,
    UserGroupsToCommsGroupsMap,
} from '../../types';
import { displayTextForUserGroup } from '../../helpers/displayHelpers';
import {
    selectOrgGroups,
    selectProviderByProviderId,
    selectUserGroupsAndCommsGroupsMapByProviderId,
} from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '../styles/globalStyles';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { CloseOutlined, InfoCircleOutlined, SettingOutlined } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { tryUpdateOrgSettings } from '@/actions/orgSettings';
import { globalMessage } from '@/utils/globalMessage';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import Tag from 'antd/lib/tag';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';

type TeamsTagInfo = {
    team: AzureActiveDirectoryTeam;
    tag: AzureTeamTag;
};

type TeamsTagsDataRow = {
    userGroup: UserGroup;
    teamsTags: string[];
};

const labelWithTooltip = (label: string, tooltip: string) => (
    <Space size={4}>
        {label}
        <Tooltip title={tooltip}>
            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
        </Tooltip>
    </Space>
);

const TEAMS_NEW_TAG_DELIMITER = '|';
// Teams channel id format: teamId#runway#channelId (used to derive default team from first channel)
const TEAMS_CHANNEL_ID_SEPARATOR = '#runway#';

interface TeamsTagsModalProps extends TeamsTagsMappingsProps {
    open: boolean;
    onCancel: () => void;
    row?: TeamsTagsDataRow;
    availableTags: TeamsTagInfo[];
    availableTeams: AzureActiveDirectoryTeam[];
    availableChannels?: { id: string }[];
}

const TeamsTagsModal: React.FC<TeamsTagsModalProps> = (props: TeamsTagsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const teamsTagsValue = Form.useWatch('teamsTags', form) ?? [];

    const updateUserGroupsToCommsGroupsMapIsLoading = useIsLoading(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );
    const updateUserGroupsToCommsGroupsMapError = useError(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );
    const updateUserGroupsToCommsGroupsMapSuccess = useUpdated(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );

    const existingUserGroupsToCommsGroupsMap = useSelector((state: RootState) =>
        selectUserGroupsAndCommsGroupsMapByProviderId(state, props.providerId, props.orgId),
    );

    const selectOptions = sortAndValidTeamsTagInfo(props.availableTags);
    const hasNewTagsToCreate = teamsTagsValue.some((v: string) => !getTeamsTagInfo(props.availableTags, v));
    const defaultTeamForNewTags = getDefaultTeamForNewTags(
        props.availableTeams,
        props.availableTags,
        props.availableChannels,
    );

    const updateUserGroupsToCommsGroupsMap = (values) => {
        if (!props.row?.userGroup) {
            return;
        }

        const teamsTagsValues: string[] = values.teamsTags ?? [];
        const teamIdForNewTags: string = values.teamIdForNewTags;

        const tagIdsOrNewTagSpecs: string[] = [];
        for (const tagValue of teamsTagsValues) {
            const existingTag = getTeamsTagInfo(props.availableTags, tagValue);
            if (existingTag) {
                tagIdsOrNewTagSpecs.push(existingTag.tag.id);
            } else {
                if (!teamIdForNewTags) {
                    globalMessage.error('Please select a team for new tags.');
                    return;
                }
                const displayName =
                    tagValue.includes(' / ') ? (tagValue.split(' / ').pop()?.trim() ?? tagValue) : tagValue;
                const normalizedDisplayName = displayName.startsWith('@') ? displayName.slice(1) : displayName;
                tagIdsOrNewTagSpecs.push(`${teamIdForNewTags}${TEAMS_NEW_TAG_DELIMITER}${normalizedDisplayName}`);
            }
        }

        const userGroupsToCommsGroupsMapChanges: UserGroupsToCommsGroupsMap = {
            [props.row.userGroup.id]: tagIdsOrNewTagSpecs,
        };

        dispatch(
            tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap, {
                orgId: props.orgId,
                providerId: props.providerId,
                userGroupsToCommsGroupsMap: {
                    ...existingUserGroupsToCommsGroupsMap,
                    ...userGroupsToCommsGroupsMapChanges,
                },
            }),
        );
    };

    React.useEffect(() => {
        if (updateUserGroupsToCommsGroupsMapError) {
            globalMessage.error(updateUserGroupsToCommsGroupsMapError);
        }
    }, [updateUserGroupsToCommsGroupsMapError]);

    React.useEffect(() => {
        if (props.open && updateUserGroupsToCommsGroupsMapSuccess) {
            globalMessage.success(`Teams tag for ${displayTextForUserGroup(props.row?.userGroup)} user group updated!`);

            props.onCancel();
        }
    }, [props.open, updateUserGroupsToCommsGroupsMapSuccess, props.row]);

    const tagRender: ({ value, closable }: CustomTagProps) => React.ReactElement = ({
        value,
        closable,
        onClose,
    }: CustomTagProps) => {
        const existingGroup = selectOptions.find((group) => getTeamsTagUIFormat(group) === value);
        const isNewTag = !existingGroup;
        const tagLabel = existingGroup != null ? getTeamsTagUIFormat(existingGroup) : `${value} (new)`;

        return (
            <Tag
                color={isNewTag ? colorWithAlpha(rwColors.primary, 0.1) : antGrey.grey3}
                closable={closable}
                closeIcon={<CloseOutlined style={{ color: antGrey.grey7 }} />}
                style={{ marginRight: 3, color: runwayColors.black }}
                onClose={onClose}
            >
                {tagLabel}
            </Tag>
        );
    };

    const initialValue = (props.row?.teamsTags ?? [])
        .map((t) => getTeamsTagInfo(props.availableTags, t))
        .filter(Boolean)
        .map((t) => getTeamsTagUIFormat(t));

    const validateTeamsTagsWithUserGroup = (_, value: string[]) => {
        return validateTeamsTag(value).then(() => {
            const tags = value ?? [];
            const hasNewTag = tags.some((v) => !getTeamsTagInfo(props.availableTags, v));
            if (hasNewTag && !(props.row?.userGroup?.members?.length ?? 0)) {
                throw new Error(
                    `Creating tags is not currently possible because the '${displayTextForUserGroup(
                        props.row?.userGroup,
                    )}' group is empty. Add members to the group to create new tags.`,
                );
            }
        });
    };

    return (
        <Modal
            title={`'${displayTextForUserGroup(props.row?.userGroup)}' group Teams tag`}
            okButtonProps={{
                loading: updateUserGroupsToCommsGroupsMapIsLoading,
                disabled: hasNewTagsToCreate && !(props.row?.userGroup?.members?.length ?? 0),
            }}
            onOk={() => form.submit()}
            okText="Save"
            open={props.open}
            onCancel={() => props.onCancel()}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form
                    onFinish={updateUserGroupsToCommsGroupsMap}
                    initialValues={{
                        teamsTags: initialValue,
                        teamIdForNewTags: defaultTeamForNewTags,
                    }}
                    form={form}
                    preserve={false}
                    layout="vertical"
                >
                    <Form.Item
                        name="teamsTags"
                        label={labelWithTooltip(
                            'Teams tag(s)',
                            "Runway will create any new tag(s) entered if they don't already exist in Teams. Users from the Runway user group will be added as members. You can manage membership from Teams at any time.",
                        )}
                        rules={[
                            {
                                validator: validateTeamsTagsWithUserGroup,
                            },
                        ]}
                    >
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Teams tag(s)"
                            tagRender={tagRender}
                            notFoundContent={
                                <Text type="secondary" style={{ padding: '8px 0' }}>
                                    Create tag
                                </Text>
                            }
                        >
                            {selectOptions.map((option) => (
                                <Select.Option key={getTeamsTagUIFormat(option)}>
                                    {getTeamsTagUIFormat(option)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {hasNewTagsToCreate && props.availableTeams.length > 0 && (
                        <Form.Item
                            name="teamIdForNewTags"
                            label={labelWithTooltip(
                                'Team for new tags',
                                'Specify which team new tags entered above will belong to. Tags will only be created if this user group has at least one member associated with a Teams account.',
                            )}
                            style={{
                                marginTop:
                                    hasNewTagsToCreate && !(props.row?.userGroup?.members?.length ?? 0) ? 40 : 24,
                            }}
                        >
                            <Select
                                placeholder="Select team for new tags"
                                allowClear={props.availableTeams.length > 1}
                                disabled={hasNewTagsToCreate && !(props.row?.userGroup?.members?.length ?? 0)}
                                options={props.availableTeams.map((team) => ({
                                    value: team.id,
                                    label: team.name,
                                }))}
                            />
                        </Form.Item>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

interface TeamsTagsMappingsProps extends OrgComponentProps {
    providerId: string;
}

export const TeamsTagsMappingsTable: React.FC<TeamsTagsMappingsProps> = (props: TeamsTagsMappingsProps) => {
    const teamsTagsMap = useSelector((state: RootState) =>
        selectUserGroupsAndCommsGroupsMapByProviderId(state, props.providerId, props.orgId),
    );

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const notificationsProvider = useSelector(
        (state: RootState) =>
            selectProviderByProviderId(state, props.orgId, props.providerId) as IntegrationProviderAzureActiveDirectory,
    );

    const availableTagsMap: Record<string, AzureTeamTag[]> = notificationsProvider?.data?.availableTags ?? {};
    const availableTeams = notificationsProvider?.data?.availableTeams ?? [];
    const availableChannels = notificationsProvider?.data?.availableChannels ?? [];
    const availableTags: TeamsTagInfo[] = [];
    for (const key of Object.keys(availableTagsMap)) {
        for (const tag of availableTagsMap[key]) {
            const team = availableTeams.find((t) => t.id == key);
            const obj = {
                team: team,
                tag: tag,
            };

            availableTags.push(obj);
        }
    }

    const teamsTagsData: TeamsTagsDataRow[] = Object.entries(teamsTagsMap ?? {}) // in case teamsTagsMap is null, pass empty object to prevent crash
        .sort()
        .map(([userGroupId, teamsTags]) => ({
            userGroup: orgGroupsById[userGroupId],
            teamsTags: teamsTags ?? [],
        }));

    const [selectedRow, setSelectedRow] = React.useState<TeamsTagsDataRow>(null);

    const renderColumnTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    const renderTeamsTag = (row: TeamsTagsDataRow) => {
        const tags = availableTags.filter((tag) => row.teamsTags.includes(tag.tag.id));
        const newTagSpecs = row.teamsTags.filter(
            (id) => id.includes(TEAMS_NEW_TAG_DELIMITER) || !tags.some((t) => t.tag.id === id),
        );
        const teamsTagsString = [
            ...tags.map((group) => getTeamsTagUIFormat(group)),
            ...newTagSpecs.map((spec) => {
                if (spec.includes(TEAMS_NEW_TAG_DELIMITER)) {
                    const [teamIdPart, displayName] = spec.split(TEAMS_NEW_TAG_DELIMITER, 2);
                    const team = availableTeams.find((t) => t.id === teamIdPart);
                    return team ? `${team.name} / @${displayName}` : spec;
                }
                return spec;
            }),
        ]
            .filter(Boolean)
            .join(', ');

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flex: 1,
                }}
            >
                {!teamsTagsString ?
                    <Text type="secondary">Not set</Text>
                :   <Text>{teamsTagsString}</Text>}
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateOrgSettings}
                    onClick={() => {
                        setSelectedRow(row);
                    }}
                    icon={<SettingOutlined style={{ color: antGrey.grey8 }} />}
                    ghost
                    disabled={false}
                />
            </div>
        );
    };

    const columns: ColumnType<TeamsTagsDataRow>[] = [
        {
            title: renderColumnTitle('Runway group'),
            dataIndex: 'userGroup',

            render: (userGroup: UserGroup) => {
                return <Text>{displayTextForUserGroup(userGroup)}</Text>;
            },
            align: 'left',
            width: '50%',
        },
        {
            title: renderColumnTitle('Teams tag(s)'),
            render: renderTeamsTag,
            align: 'left',
            width: '50%',
        },
    ];

    return !teamsTagsMap ? null : (
            <>
                <div
                    style={{
                        display: 'flex',
                        gap: 20,
                        alignItems: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 20,
                            color: antGrey.grey7,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Microsoft Teams Tags
                    </Text>
                    <div
                        style={{
                            flex: 1,
                            height: '100%',
                        }}
                    >
                        <Divider
                            type="horizontal"
                            style={{
                                width: '100%',
                                margin: 0,
                            }}
                        />
                    </div>
                </div>
                <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 15 }}>
                    <Space>
                        <Text type="secondary">
                            Configure the Teams tag(s) that correspond to each Runway user group.
                        </Text>
                    </Space>
                    <Table
                        size="middle"
                        columns={columns}
                        dataSource={teamsTagsData}
                        pagination={{ hideOnSinglePage: true, defaultPageSize: 5, size: 'default' }}
                    />
                    <TeamsTagsModal
                        open={!!selectedRow}
                        providerId={props.providerId}
                        row={selectedRow}
                        availableTags={availableTags}
                        availableTeams={availableTeams}
                        availableChannels={availableChannels}
                        orgId={props.orgId}
                        onCancel={() => setSelectedRow(null)}
                    />
                </Space>
            </>
        );
};

const getTeamsTagUIFormat: (tagInfo: TeamsTagInfo) => string = (tagInfo) => {
    if (!isValidTagInfo(tagInfo)) {
        return '';
    }

    const teamsTag = tagInfo.tag.displayName;
    return `${tagInfo.team.name} / ${teamsTag[0] == '@' ? teamsTag : `@${teamsTag}`}`;
};

const getTeamsTagInfo: (tagInfos: TeamsTagInfo[], identifier: string) => TeamsTagInfo = (tagInfos, identifier) => {
    return tagInfos.find((tagInfo) => tagInfo.tag.id == identifier || getTeamsTagUIFormat(tagInfo) == identifier);
};

const isValidTagInfo: (tagInfo: TeamsTagInfo) => boolean = (tagInfo) => {
    return (
        tagInfo &&
        tagInfo.team &&
        tagInfo.tag &&
        tagInfo.team.name.trim().length > 0 &&
        tagInfo.tag.displayName.trim().length > 0
    );
};

const sortAndValidTeamsTagInfo: (tagInfos: TeamsTagInfo[]) => TeamsTagInfo[] = (tagInfos) => {
    return tagInfos
        .sort((a, b) => {
            if (a.tag.displayName < b.tag.displayName) {
                return -1;
            }
            if (a.tag.displayName > b.tag.displayName) {
                return 1;
            }
            return 0;
        })
        .filter((tag) => isValidTagInfo(tag));
};

const validateTeamsTag = (value: string[]) => {
    const tags = value ?? [];
    const invalidTeamsTagHandles = [];
    const regex = /^[A-z0-9.\-_@\/\s]+$/;

    for (const teamsTagHandle of tags) {
        if (!regex.test(teamsTagHandle)) {
            invalidTeamsTagHandles.push(teamsTagHandle);
        }
    }

    if (invalidTeamsTagHandles.length > 0) {
        return Promise.reject(`Invalid character(s) in Teams tag handle(s): ${invalidTeamsTagHandles.join(', ')}`);
    }

    return Promise.resolve();
};

const getDefaultTeamForNewTags = (
    availableTeams: AzureActiveDirectoryTeam[],
    availableTags: TeamsTagInfo[],
    availableChannels?: { id: string }[],
): string | undefined => {
    if (availableTeams.length === 0) return undefined;
    if (availableTeams.length === 1) return availableTeams[0].id;
    // If all available tags are from one team, use that team
    const uniqueTeamIds = new Set(availableTags.map((t) => t.team?.id).filter(Boolean));
    if (uniqueTeamIds.size === 1) {
        const teamId = [...uniqueTeamIds][0];
        if (availableTeams.some((t) => t.id === teamId)) return teamId;
    }
    // Use first channel's team (id format: teamId#runway#channelId)
    const firstChannel = availableChannels?.[0]?.id;
    if (firstChannel?.includes(TEAMS_CHANNEL_ID_SEPARATOR)) {
        const teamId = firstChannel.split(TEAMS_CHANNEL_ID_SEPARATOR)[0];
        if (availableTeams.some((t) => t.id === teamId)) return teamId;
    }
    return availableTeams[0]?.id;
};
